/**
 * ローディング画面モジュール
 * 
 * @package LIVMO
 * @version 1.0.0
 */

class LoadingScreen {
  constructor() {
    this.loadingScreen = document.getElementById('loading-screen');
    this.texts = document.querySelectorAll('.loading-screen__text');
    this.indicator = document.querySelector('.loading-screen__indicator');
    this.topBackground = document.querySelector('.loading-screen__background--top');
    this.bottomBackground = document.querySelector('.loading-screen__background--bottom');
    this.heroReadyPromise = null;
    this.heroStartDispatched = false;
    this.scrollAligned = false;
    
    // トップページかどうかを判定
    this.isHomePage = this.checkIsHomePage();
    
    
    // アニメーション設定
    this.config = {
      backgroundDuration: this.isHomePage ? 3.5 : 1.5, // トップページは長め、下層ページは短め
      textStagger: 0.02,
      textDuration: 0.4,
      textDisplayDuration: 0.6,
      textHideDuration: 0.3,
      fadeOutDuration: 0.6,
      // 背景の縮小率と文字表示のタイミング
      backgroundStages: [
        { scale: 0.8, textIndex: 0, delay: 0.8 }, // 上下20%のとき最初の文字（背景が縮んでから）
        { scale: 0.5, textIndex: 1, delay: 1.8 }, // 上下50%のとき2番目の文字
        { scale: 0.2, textIndex: 2, delay: 2.8 }  // 上下80%のとき最後の文字
      ]
    };
    
    // テキストデータ
    this.textData = [
      { text: 'Living is more', duration: 1.5 },
      { text: 'くらしを豊かに', duration: 1.2 },
      { text: 'LIVMO.inc', duration: 1.2 }
    ];
    
    this.init();
  }
  
  /**
   * トップページかどうかを判定
   */
  checkIsHomePage() {
    const pathname = window.location.pathname;
    
    // 1. body要素にis-front-pageクラスがあるかチェック（PHP側で追加）
    const bodyHasFrontPageClass = document.body && document.body.classList.contains('is-front-page');
    
    // 2. body要素にhomeクラスがあるかチェック（WordPressが自動的に追加）
    const bodyHasHomeClass = document.body && document.body.classList.contains('home');
    
    // 3. パスが / または /index.php の場合
    const isRootPath = pathname === '/' || 
           pathname === '/index.php' || 
           pathname === '/index.html' ||
           pathname.endsWith('/index.php') ||
           pathname.endsWith('/index.html');
    
    // 4. フロントページテンプレートが使用されているかチェック（front-page.php）
    const hasFrontPageTemplate = document.body && document.body.classList.contains('page-template-front-page');
    
    const isHomePage = bodyHasFrontPageClass || bodyHasHomeClass || isRootPath || hasFrontPageTemplate;
    
    return isHomePage;
  }
  
  /**
   * 初期化
   */
  init() {
    if (!this.loadingScreen) {
      return;
    }
    
    
    // メインコンテンツを初期状態に設定（非表示）
    this.prepareMainContent();
    
    // ローディング画面を表示
    this.showLoadingScreen();
    
    // アニメーション開始
    this.startAnimation();
  }
  
  /**
   * メインコンテンツを初期状態に設定
   */
  prepareMainContent() {
    const mainContent = document.querySelector('.main-container');
    this.ensureScrollTop();
    
    // ローディング画面表示中はLenisを停止
    if (window.lenis && window.lenis.isInitialized) {
      window.lenis.stop();
    }
    
    if (mainContent) {
      // 初期状態で非表示に設定（直接スタイルで設定）
      mainContent.style.opacity = '0';
      mainContent.style.visibility = 'hidden';
      mainContent.style.transform = 'translateY(15px) scale(0.98)'; // 下から上に移動、縮小状態
    } else {
    }
    
    // ロゴとメニューも初期状態で非表示に設定
    const logo = document.querySelector('.logo');
    const menu = document.querySelector('.menu');
    if (logo) {
      logo.style.opacity = '0';
      logo.style.visibility = 'hidden';
    }
    if (menu) {
      menu.style.opacity = '0';
      menu.style.visibility = 'hidden';
    }
  }

  /**
   * メインコンテンツの最終状態を整える
   * ScrollTriggerのpinがずれないようtransform等のインラインスタイルをクリア
   */
  finalizeMainContentStyles(targetElement = null) {
    const mainContent = targetElement || document.querySelector('.main-container');
    if (!mainContent) {
      return;
    }

    // 表示用クラスを付与
    mainContent.classList.add('main-container--visible');

    // GSAPが利用可能ならclearPropsでtransform等をリセット
    if (typeof gsap !== 'undefined') {
      gsap.set(mainContent, { clearProps: 'transform,opacity,visibility' });
    } else {
      mainContent.style.removeProperty('transform');
      mainContent.style.removeProperty('opacity');
      mainContent.style.removeProperty('visibility');
    }

    // 念のため固定ナビゲーションも表示状態に整える
    this.ensureFixedNavigationVisible();
  }
  
  waitForHeroReady(timeout = 1800) {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }
    if (window.__heroReady) {
      return Promise.resolve();
    }
    if (this.heroReadyPromise) {
      return this.heroReadyPromise;
    }
    this.heroReadyPromise = new Promise(resolve => {
      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        window.removeEventListener('hero:ready', handler);
        resolve();
      };
      const handler = () => done();
      window.addEventListener('hero:ready', handler, { once: true });
      setTimeout(() => done(), timeout);
    });
    return this.heroReadyPromise;
  }

  ensureScrollTop(force = false) {
    if (typeof window === 'undefined') {
      return;
    }
    const alreadyAligned = this.scrollAligned && !force;
    const currentY = window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    if (currentY === 0 && alreadyAligned) {
      return;
    }
    const alignNow = () => {
      window.scrollTo(0, 0);
      this.scrollAligned = true;
    };
    if (currentY !== 0) {
      requestAnimationFrame(alignNow);
      alignNow();
    } else if (!alreadyAligned) {
      alignNow();
    }
  }

  triggerHeroStart() {
    if (this.heroStartDispatched) {
      return;
    }
    this.waitForHeroReady()
      .then(() => {
        if (this.heroStartDispatched) {
          return;
        }
        this.heroStartDispatched = true;
        try {
          window.dispatchEvent(new CustomEvent('hero:start'));
        } catch (e) {
        }
      })
      .catch(() => {});
  }
  
  revealMainContent(mainContent) {
    if (!mainContent) {
      return;
    }
    this.ensureScrollTop();
    const duration = this.config.fadeOutDuration;
    const startFade = () => {
      mainContent.style.visibility = 'visible';
      if (typeof gsap !== 'undefined') {
        gsap.to(mainContent, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration,
          ease: "power2.out",
          onComplete: () => {
            this.finalizeMainContentStyles(mainContent);
          }
        });
      } else {
        mainContent.style.opacity = '1';
        mainContent.style.transform = 'translateY(0) scale(1)';
        this.finalizeMainContentStyles(mainContent);
      }
      this.animateFixedNavigation(null, 0, duration);
    };
    startFade();
  }
  
  /**
   * ローディング画面を表示
   */
  showLoadingScreen() {
    this.loadingScreen.classList.add('loading-screen--active');
    
    // トップページの場合のみテキスト要素を表示
    if (this.isHomePage) {
      this.texts.forEach(text => {
        text.classList.add('loading-screen__text--active');
      });
    } else {
      // 下層ページではテキストを非表示
      this.texts.forEach(text => {
        text.style.display = 'none';
      });
      // インジケーターも非表示
      if (this.indicator) {
        this.indicator.style.display = 'none';
      }
    }
  }
  
  /**
   * アニメーション開始
   */
  startAnimation() {
    
    // GSAPが読み込まれているかチェック
    if (typeof gsap === 'undefined') {
      this.skipAnimation();
      return;
    }
    
    
    // 要素の存在確認
    
    const tl = gsap.timeline();
    
    
    if (this.isHomePage) {
      // トップページ: フルアニメーション（背景 + テキスト）
      this.startFullAnimation(tl);
    } else {
      // 下層ページ: 背景アニメーションのみ
      this.startBackgroundOnlyAnimation(tl);
    }
  }
  
  /**
   * フルアニメーション（トップページ用）
   */
  startFullAnimation(tl) {
    
    // メインコンテンツを再度取得（確実に取得するため）
    const mainContent = document.querySelector('.main-container');
    const backgroundCompleteTime = 4.0; // 背景アニメーションの完了時間（4.0秒）
    
    if (mainContent) {
      try {
        const initialOpacity = parseFloat(window.getComputedStyle(mainContent).opacity) || 0;
      } catch (e) {
      }
    }
    
    // 1. 背景の連続的な縮小アニメーション（完全に消えるまで）
    tl.to([this.topBackground, this.bottomBackground], {
      scaleY: 0,
      duration: backgroundCompleteTime,
      ease: "power2.inOut",
    });
    
    // 2. 文字アニメーション（背景と同期）
    this.config.backgroundStages.forEach((stage, index) => {
      const textElement = this.texts[stage.textIndex];
      const letters = textElement ? textElement.querySelectorAll('span') : [];
      
      if (letters.length > 0) {
        // 文字の表示アニメーション（流れるようなスロット回転）
        tl.to(letters, {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: this.config.textDuration,
          stagger: this.config.textStagger,
          ease: "sine.out",
        }, stage.delay);
        
        // 文字の表示時間
        tl.to({}, { 
          duration: this.config.textDisplayDuration 
        }, stage.delay + this.config.textDuration);
        
        // 文字の非表示アニメーション（流れるようなスロット回転）
        tl.to(letters, {
          opacity: 0,
          y: 100,
          rotationX: -90,
          duration: this.config.textHideDuration,
          stagger: this.config.textStagger,
          ease: "sine.in",
        }, stage.delay + this.config.textDuration + this.config.textDisplayDuration);
      }
    });
    
    // 3. LODINGテキスト非表示（流れるようなフェードアウト）
    if (this.indicator) {
      tl.to(this.indicator, {
        opacity: 0,
        duration: 0.4,
        ease: "sine.out"
      }, 3.4);
    }
    
    // 4. メインコンテンツのフェードイン開始（背景アニメーション完了少し前から開始）
    if (mainContent) {
      // 確実に初期状態を設定（GSAPを使わずに直接設定）
      mainContent.style.opacity = '0';
      mainContent.style.visibility = 'hidden';
      mainContent.style.transform = 'translateY(15px) scale(0.98)'; // 下から上に移動、縮小状態
      
      const fadeInStartTime = backgroundCompleteTime - this.config.fadeOutDuration * 0.5;
      
      const heroStartLead = 0.25;
      const heroStartTime = Math.max(0, backgroundCompleteTime - heroStartLead);
      tl.call(() => this.triggerHeroStart(), null, heroStartTime);
      
      tl.call(() => {
        this.waitForHeroReady().then(() => {
          this.revealMainContent(mainContent);
        });
      }, null, fadeInStartTime);
    } else {
    }
    
    // 5. ローディング画面のフェードアウト（背景アニメーション完了と同時に開始）
    const lastStage = this.config.backgroundStages[this.config.backgroundStages.length - 1];
    const lastTextCompleteTime = lastStage.delay + this.config.textDuration + this.config.textDisplayDuration + this.config.textHideDuration;
    const loadingTextCompleteTime = 3.4 + 0.4; // LODINGテキストの完了時間
    const allAnimationsCompleteTime = Math.max(lastTextCompleteTime, loadingTextCompleteTime, backgroundCompleteTime);
    
    
    tl.to(this.loadingScreen, {
      opacity: 0,
      visibility: 'hidden',
      duration: this.config.fadeOutDuration,
      ease: "sine.out",
      onComplete: () => {
        this.hideLoadingScreen();
      }
    }, backgroundCompleteTime);
  }
  
  /**
   * 背景アニメーションのみ（下層ページ用）
   */
  startBackgroundOnlyAnimation(tl) {
    
    // メインコンテンツを取得
    const mainContent = document.querySelector('.main-container');
    const backgroundCompleteTime = this.config.backgroundDuration;
    const fadeInDuration = this.config.fadeOutDuration;
    
    if (!mainContent) {
    }
    
    // 1. 背景の連続的な縮小アニメーション（完全に消えるまで）
    tl.to([this.topBackground, this.bottomBackground], {
      scaleY: 0,
      duration: backgroundCompleteTime,
      ease: "power2.inOut",
    });
    
    // 2. メインコンテンツのフェードイン（背景アニメーション完了時に開始）
      if (mainContent) {
        // 初期状態を設定（下から上に移動、縮小状態）
        mainContent.style.transform = 'translateY(5px) scale(0.97)';
        
        // 背景アニメーション完了時にvisibilityを表示にしてからフェードイン
        tl.call(() => {
          mainContent.style.visibility = 'visible';
        }, null, backgroundCompleteTime);
        
        this.ensureScrollTop();
        
        tl.call(() => this.triggerHeroStart(), null, Math.max(0, backgroundCompleteTime - 0.2));
        
        // フェードインアニメーション（下から上に移動しながら、縮小から拡大）
        tl.to(mainContent, {
          opacity: 1,
          y: 0, // translateY(0)に戻す（下から上に移動）
          scale: 1, // scale(1)に戻す（縮小から通常サイズへ）
          duration: fadeInDuration,
          ease: "power2.out",
          onStart: () => {
          },
          onComplete: () => {
            this.finalizeMainContentStyles(mainContent);
          }
        }, backgroundCompleteTime);

        // 固定ナビゲーション（ロゴ・メニュー）も同タイミングで表示
        this.animateFixedNavigation(tl, backgroundCompleteTime, fadeInDuration);
      }
    
    // 3. ローディング画面のフェードアウト（背景アニメーション完了と同時に開始）
    tl.to(this.loadingScreen, {
      opacity: 0,
      visibility: 'hidden',
      duration: fadeInDuration,
      ease: "sine.out",
      onComplete: () => {
        this.hideLoadingScreen();
      }
    }, backgroundCompleteTime);
  }
  
  /**
   * ローディング画面を非表示
   */
  hideLoadingScreen() {
    const finalizeHide = () => {
      if (this.loadingScreen) {
        this.loadingScreen.style.display = 'none';
      }
      this.showMainContent();
    };
    
    this.waitForHeroReady()
      .catch(() => {})
      .finally(finalizeHide);
  }
  
  /**
   * メインコンテンツを表示
   */
  showMainContent() {
    // メインコンテンツの表示処理（既にGSAPでアニメーション済み）
    const mainContent = document.querySelector('.main-container');
    if (mainContent) {
      this.finalizeMainContentStyles(mainContent);

      // メインコンテンツ表示後にLenisを再開
      if (window.lenis && window.lenis.isInitialized) {
        window.lenis.start();
      }

      // イベント通知
      const notifyShown = () => {
        try {
          window.dispatchEvent(new CustomEvent('app:main-shown'));
        } catch (_) {}
      };
      
      // 少し遅延させてイベントを発火（アニメーション完了を確実にする）
      setTimeout(() => {
        notifyShown();
      }, 100);
    }
  }
  
  /**
   * アニメーションをスキップ
   */
  skipAnimation() {
    if (this.loadingScreen) {
      this.loadingScreen.style.display = 'none';
      this.showMainContent();
    }
  }

  /**
   * 固定ナビゲーション（ロゴ・メニュー）をアニメーション表示
   */
  animateFixedNavigation(tl, startTime, duration = 0.4) {
    const logo = document.querySelector('.logo');
    const menu = document.querySelector('.menu');
    
    if (!logo && !menu) {
      return;
    }
    
    if (typeof gsap === 'undefined') {
      if (logo) {
        logo.style.visibility = 'visible';
        logo.style.opacity = '1';
      }
      if (menu) {
        menu.style.visibility = 'visible';
        menu.style.opacity = '1';
      }
      return;
    }

    // ロゴ：左から右にアニメーション
    if (logo) {
      gsap.set(logo, {
      autoAlpha: 0,
      x: -60,
      visibility: 'hidden'
    });

      const logoTweenConfig = {
      autoAlpha: 1,
      x: 0,
      duration,
      ease: 'power3.out',
      onStart: () => {
          logo.style.visibility = 'visible';
      }
    };

    if (tl) {
        tl.to(logo, logoTweenConfig, startTime);
      } else {
        gsap.to(logo, logoTweenConfig);
      }
    }

    // メニュー：右から左にアニメーション（スマホ時）
    if (menu) {
      const isMobile = window.innerWidth <= 767;
      
      gsap.set(menu, {
        autoAlpha: 0,
        x: isMobile ? 60 : -60, // スマホ時は右側から、デスクトップ時は左側から
        visibility: 'hidden'
      });

      const menuTweenConfig = {
        autoAlpha: 1,
        x: 0,
        duration,
        ease: 'power3.out',
        delay: logo ? 0.05 : 0, // ロゴがある場合は少し遅延
        onStart: () => {
          menu.style.visibility = 'visible';
        }
      };

      if (tl) {
        tl.to(menu, menuTweenConfig, startTime);
    } else {
        gsap.to(menu, menuTweenConfig);
      }
    }
  }

  /**
   * 固定ナビゲーションを即時表示（フェールセーフ）
   */
  ensureFixedNavigationVisible() {
    const fixedElements = document.querySelectorAll('.logo, .menu');
    if (!fixedElements.length) {
      return;
    }

    fixedElements.forEach(el => {
      el.style.visibility = 'visible';
      el.style.opacity = '1';
    });
  }
  
  /**
   * リセット（デバッグ用）
   */
  reset() {
    if (this.loadingScreen) {
      this.loadingScreen.style.display = 'block';
      this.loadingScreen.classList.remove('loading-screen--active', 'loading-screen--fadeout');
      
      if (typeof gsap !== 'undefined') {
        // 背景をリセット
        gsap.set([this.topBackground, this.bottomBackground], { scaleY: 1 });
        
        // テキストをリセット
        this.texts.forEach(text => {
          const letters = text.querySelectorAll('span');
          gsap.set(letters, {
            opacity: 0,
            x: -50,
            rotationX: 90
          });
        });
        
        // LODINGテキストをリセット
        gsap.set(this.indicator, { opacity: 1 });
      }
    }
  }
}

// ページ読み込み完了時にローディング画面を開始
document.addEventListener('DOMContentLoaded', () => {
  // ローディング画面が存在する場合のみ実行
  if (document.getElementById('loading-screen')) {
    new LoadingScreen();
  } else {
    // ローディング画面が存在しない場合でも、ロゴとメニューを表示
    const logo = document.querySelector('.logo');
    const menu = document.querySelector('.menu');
    if (logo) {
      if (typeof gsap !== 'undefined') {
        gsap.to(logo, {
          opacity: 1,
          visibility: 'visible',
          duration: 0.4,
          ease: 'power2.out'
        });
      } else {
        logo.style.opacity = '1';
        logo.style.visibility = 'visible';
      }
    }
    if (menu) {
      if (typeof gsap !== 'undefined') {
        gsap.to(menu, {
          opacity: 1,
          visibility: 'visible',
          duration: 0.4,
          ease: 'power2.out'
        });
      } else {
        menu.style.opacity = '1';
        menu.style.visibility = 'visible';
      }
    }
  }
});

// デバッグ用（開発時のみ）
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  window.LoadingScreen = LoadingScreen;
}
